import { sql } from '@w/db'
import { pageRelationBodies } from './page-relations-triggers-ddl'
import { cardDerivationStatements, pageDeletionStatements, txApplied, type DerivationArgs } from './card-derivation'

/**
 * Write-path page relations (db-crdt-sync requirements :47, :49; flashcards-spaces.decisions.md 2026-10-25 option A,
 * step 4). Both write paths run the page relation bodies in the commit's transaction, after the page write: the update
 * body over the page's applied rows in the transaction (F13 crdt_tx_applied), and the lifecycle body, which reads the
 * page's links from the index, for a creation, a delete and a restore. These statements execute only in the author transaction.
 */
type Body = 'update' | 'lifecycle'

const tokens = /(crdt_applied\(NEW\.data\)|NEW\.rowid|NEW\.deletedAt|NEW\.id)/

function statementsOf(body: Body) {
  const statements = pageRelationBodies[body].split(/;\s*(?=INSERT\b|$)/).map(text => text.trim()).filter(Boolean)
  if (statements.length !== 2) throw new Error(`INV: relation ${body} has ${statements.length} statements`)
  return statements.map(text => text.split(tokens))
}
const bodies = { update: statementsOf('update'), lifecycle: statementsOf('lifecycle') }

/** Bind the page and its transaction CDC to the relation statements. */
export function pageRelationStatements(body: Body, pageId: string) {
  return bodies[body].map(parts => {
    const strings: string[] = [], values: unknown[] = []
    for (const [index, part] of parts.entries()) {
      if (index % 2 === 0) { strings.push(part); continue }
      values.push(part === 'NEW.id' ? pageId
        : part === 'NEW.rowid' ? sql`(SELECT rowid FROM pages WHERE id=${pageId})`
        : part === 'NEW.deletedAt' ? sql`(SELECT deletedAt FROM pages WHERE id=${pageId})`
        : txApplied(pageId))
    }
    return sql(Object.assign(strings, { raw: strings }), ...values)
  })
}

/** A delete or restore of the page (deletedAt in the commit's fields): its author's cards and its links. */
export function pageLifecycleStatements(deletedAt: number | null | undefined, derivation: DerivationArgs) {
  if (deletedAt === undefined) return []
  return [...deletedAt === null ? cardDerivationStatements({ kind: 'page' }, derivation) : pageDeletionStatements(derivation),
    ...pageRelationStatements('lifecycle', derivation.pageId)]
}
